import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  CreateSubscriptionDto,
  ReportLanguage,
  ReportType,
  Schedule,
  SubscriptionPayload,
} from 'src/app/shared/utils/reports';

export type ReportKind = 'entity' | 'portfolio' | 'query';

export interface ResolvedQuery {
  name: string;
  searchQuery: Record<string, unknown>;
}

export interface ReportFormData {
  id?: string;
  kind?: ReportKind;
  active: boolean;
  reportObject?: string;
  portfolioName?: string;
  entityName?: string;
  queryName?: string;
  schedule?: Schedule;
  reportType: ReportType | '';
  recipients: string[];
  isNew: boolean;
  /** Kept from the stored subscription so an edit doesn't reset it to the
   *  editor's browser locale. Absent on new reports. */
  language?: ReportLanguage;
}

@Injectable({
  providedIn: 'root',
})
export class ReportsSetupFormService {
  private defaultReportForm: ReportFormData = {
    id: undefined,
    kind: 'entity',
    active: true,
    reportObject: undefined,
    schedule: undefined,
    reportType: '',
    recipients: [],
    isNew: true,
  };

  private currentReportSubject = new BehaviorSubject<ReportFormData>(
    this.defaultReportForm,
  );
  public currentReport$ = this.currentReportSubject.asObservable();

  constructor() {}

  getCurrentReport(): ReportFormData {
    return this.currentReportSubject.getValue();
  }

  setCurrentReport(report: ReportFormData): void {
    this.currentReportSubject.next(report);
  }

  createNewReport(): void {
    this.currentReportSubject.next({ ...this.defaultReportForm });
  }

  resetForm(): void {
    this.currentReportSubject.next({ ...this.defaultReportForm });
  }

  setupReportFromSubscription(subscription: ReportFormData): void {
    const reportForm: ReportFormData = {
      id: subscription.id,
      kind: subscription.kind || 'entity',
      active: subscription.active,
      entityName: subscription.entityName,
      portfolioName: subscription.portfolioName,
      queryName: subscription.queryName,
      reportObject: subscription.reportObject,
      schedule: subscription.schedule,
      reportType: subscription.reportType,
      recipients: subscription.recipients,
      isNew: false,
      language: subscription.language,
    };

    this.setCurrentReport(reportForm);
  }

  convertFormToSubscription(
    formData: ReportFormData,
    portfolioName?: string,
    companyIds?: string[],
    resolvedQuery?: ResolvedQuery,
  ): CreateSubscriptionDto & { id?: string; active?: boolean } {
    if (!formData.reportType) {
      throw new Error('reportType is required to build a subscription DTO');
    }
    const dto: CreateSubscriptionDto & { id?: string; active?: boolean } = {
      schedule: (formData.schedule ?? 'daily') as Schedule,
      reportType: formData.reportType,
      reportFormat: 'pdf',
      recipients: formData.recipients,
      payload: this.buildPayload(
        formData,
        portfolioName,
        companyIds,
        resolvedQuery,
      ),
    };

    if (formData.id) {
      dto.id = formData.id;
      dto.active = formData.active;
    }

    return dto;
  }

  private buildPayload(
    formData: ReportFormData,
    portfolioName?: string,
    companyIds?: string[],
    resolvedQuery?: ResolvedQuery,
  ): SubscriptionPayload {
    if (formData.kind === 'portfolio') {
      return {
        kind: 'portfolio',
        portfolioId: formData.reportObject || '',
        portfolioName: portfolioName ?? formData.portfolioName ?? '',
        companyIds: companyIds ?? [],
        language: formData.language,
      };
    }

    if (formData.kind === 'query') {
      return {
        kind: 'query',
        queryName: resolvedQuery?.name ?? formData.queryName ?? '',
        searchQuery: resolvedQuery?.searchQuery ?? {},
        language: formData.language,
      };
    }

    const companyId = (formData.reportObject || '').replace(/^company:/, '');
    return {
      kind: 'company',
      companyId,
      language: formData.language,
    };
  }
}
